import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { appConfig } from "../../config/app";
import Loading from "../../app/ui/Loading/loading";
import hyperPayService from "../../services/hyperPayService";
import { downloadReceipt, formatPaymentDataForReceipt } from "../../utils/receiptGenerator";

const PaymentResult = () => {
  const [loading, setLoading] = useState(true);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [playerDetails, setPlayerDetails] = useState(null);
  const [error, setError] = useState(null);
  const [downloadingReceipt, setDownloadingReceipt] = useState(false);
  const callbackCalledRef = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Function to fetch player details by transaction ID
  const fetchPlayerDetails = async (transactionId) => {
    try {
      console.log("Fetching player details for transaction ID:", transactionId);
      
      const response = await axios.get(
        `${appConfig.DotNet_Api_Endpoint}/OnlinePayment/payment-details/by-transaction/${transactionId}`,
        {
          headers: {
            Authorization: `Token ${localStorage.getItem("userToken")}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Player details response:", response.data);
      setPlayerDetails(prevDetails => ({
        ...prevDetails,
        ...response.data
      }));
    } catch (err) {
      console.error("Error fetching player details:", err);
      // Don't set error state here as we don't want to break the payment result page
      // if player details can't be fetched
    }
  };

  useEffect(() => {
    // Clean up any existing HyperPay widget when this component mounts
    console.log("PaymentResult component mounted, cleaning up HyperPay widget");
    hyperPayService.destroyWidget();
    
    // Force cleanup as backup
    setTimeout(() => {
      hyperPayService.forceCleanup();
    }, 500);
    
    const fetchPaymentDetails = async () => {
      if (callbackCalledRef.current) return;
      callbackCalledRef.current = true;

      try {
        const urlParams = new URLSearchParams(location.search);
        const checkoutId = urlParams.get('id'); // HyperPay sends 'id' parameter
        const resourcePath = urlParams.get('resourcePath'); // HyperPay sends 'resourcePath' parameter
        const errorParam = urlParams.get('error'); // Error from our callback page
        
        // MyFatoorah parameters
        const myFatoorahRef = urlParams.get('ref');
        const myFatoorahPaymentId = urlParams.get('paymentId');
        const myFatoorahId = urlParams.get('Id');
        
        // Fallback for manual testing
        const manualStatus = urlParams.get('status');

        console.log("Payment result URL params:", { 
          checkoutId, 
          resourcePath,
          errorParam,
          myFatoorahRef,
          myFatoorahPaymentId,
          myFatoorahId,
          manualStatus,
          fullSearch: location.search,
          fullUrl: window.location.href
        });

        // Check for error from our callback page
        if (errorParam) {
          throw new Error(`Payment callback error: ${errorParam}`);
        }

        // Check if this is a MyFatoorah callback (has ref or paymentId)
        if (myFatoorahRef || myFatoorahPaymentId || myFatoorahId) {
          console.log("Processing MyFatoorah callback with paymentId:", myFatoorahPaymentId || myFatoorahId);
          
          // Call the backend to get payment status and process the callback
          const statusResponse = await axios.get(
            `${appConfig.DotNet_Api_Endpoint}/OnlinePayment/myfatoorah/callback?paymentId=${myFatoorahPaymentId || myFatoorahId}`,
            {
              headers: {
                Authorization: `Token ${localStorage.getItem("userToken")}`,
                "Content-Type": "application/json",
              },
            }
          );

          console.log("MyFatoorah callback response:", statusResponse.data);
          setPaymentDetails(statusResponse.data);
          
          // If payment is successful, fetch player details
          if (statusResponse.data?.isSuccessful && statusResponse.data?.id) {
            await fetchPlayerDetails(statusResponse.data.id);
            // Also store payment transaction details for display
            setPlayerDetails(prevDetails => ({
              ...prevDetails,
              transactionId: statusResponse.data.id,
              transactionDate: statusResponse.data.timestamp || new Date().toISOString(),
              amountPaid: statusResponse.data.amount,
              currency: statusResponse.data.currency || 'KWD',
              // Use callback response data as fallback if player details are not available
              playerName: prevDetails?.playerName || statusResponse.data.playerName,
              membershipName: prevDetails?.membershipName || statusResponse.data.membershipName,
              membershipId: prevDetails?.membershipId || statusResponse.data.membershipId
            }));
          }
          
        } else if (resourcePath && checkoutId) {
          // If we have resourcePath (normal HyperPay callback), use it
          console.log("Processing HyperPay callback with resourcePath:", resourcePath);
          
          // Call the backend callback endpoint to verify payment
          const callbackResponse = await axios.post(
            `${appConfig.DotNet_Api_Endpoint}/OnlinePayment/hyperpay/callback`,
            {
              CheckoutId: checkoutId,
              ResourcePath: resourcePath,
            },
            {
              headers: {
                Authorization: `Token ${localStorage.getItem("userToken")}`,
                "Content-Type": "application/json",
              },
            }
          );

          console.log("Callback response:", callbackResponse.data);
          setPaymentDetails(callbackResponse.data);
          
          // If payment is successful, fetch player details
          if (callbackResponse.data?.isSuccessful && callbackResponse.data?.id) {
            await fetchPlayerDetails(callbackResponse.data.id);
            // Also store payment transaction details for display
            setPlayerDetails(prevDetails => ({
              ...prevDetails,
              transactionId: callbackResponse.data.id,
              transactionDate: callbackResponse.data.timestamp || new Date().toISOString(),
              amountPaid: callbackResponse.data.amount,
              currency: callbackResponse.data.currency || 'SAR',
              // Use callback response data as fallback if player details are not available
              playerName: prevDetails?.playerName || callbackResponse.data.playerName,
              membershipName: prevDetails?.membershipName || callbackResponse.data.membershipName,
              membershipId: prevDetails?.membershipId || callbackResponse.data.membershipId
            }));
          }
          
        } else if (manualStatus) {
          // Handle manual navigation (testing purposes)
          console.log("Processing manual status check");
          
          setPaymentDetails({
            isSuccessful: manualStatus === 'success',
            message: `Payment ${manualStatus}`,
            checkoutId: 'manual'
          });
          
        } else {
          // Wait a bit in case the parameters are still being processed by the browser
          console.log("Missing parameters, waiting 2 seconds for potential browser processing...");
          setTimeout(() => {
            const retryParams = new URLSearchParams(window.location.search);
            const retryCheckoutId = retryParams.get('id');
            const retryResourcePath = retryParams.get('resourcePath');
            
            if (retryResourcePath && retryCheckoutId) {
              // Retry with the now-available parameters
              console.log("Parameters found after retry, processing...");
              fetchPaymentDetails();
              return;
            }
            
            throw new Error("Invalid payment callback - missing required parameters (id and resourcePath). Please ensure you accessed this page through a valid payment flow.");
          }, 2000);
          return; // Exit early, will retry after timeout
        }
        
      } catch (err) {
        console.error("Payment result error:", err);
        console.error("Error details:", {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status
        });
        
        setError(
          err.response?.data?.message || 
          err.message || 
          "Failed to get payment details. Please contact support."
        );
        setLoading(false);
      } finally {
        if (!error) {
          setLoading(false);
        }
      }
    };

    fetchPaymentDetails();
  }, [location.search]);

  // Cleanup effect when component unmounts
  useEffect(() => {
    return () => {
      // Clean up HyperPay widget when leaving this page
      hyperPayService.destroyWidget();
    };
  }, []);

  // Add window event listener for cleanup
  useEffect(() => {
    const handleBeforeUnload = () => {
      hyperPayService.forceCleanup();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is being hidden, clean up HyperPay widget
        hyperPayService.destroyWidget();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Auto-preview receipt when payment is successful
  useEffect(() => {
    if (paymentDetails && paymentDetails.isSuccessful && !loading) {
      // Receipt will be displayed directly on the page, no need for auto-preview
      console.log("Payment successful, receipt will be displayed on page");
    }
  }, [paymentDetails, loading]);

  const handleReturnHome = () => {
    navigate("/");
  };

  const handleTryAgain = () => {
    navigate(-1); // Go back to payment page
  };

  const handleDownloadReceipt = async () => {
    setDownloadingReceipt(true);
    try {
      const formattedData = formatPaymentDataForReceipt(paymentDetails);
      const success = downloadReceipt(formattedData, playerDetails);
      if (success) {
        // Show success message for a brief moment
        setTimeout(() => {
          setDownloadingReceipt(false);
        }, 1000);
      } else {
        setDownloadingReceipt(false);
        alert('Error generating receipt. Please try again.');
      }
    } catch (error) {
      setDownloadingReceipt(false);
      console.error('Receipt download error:', error);
      alert('Error generating receipt. Please try again.');
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <div className="min-vh-100 bg-light d-flex flex-column">
        {/* Header */}
        <div
          className="text-white py-0 px-4 d-flex align-items-center"
          style={{ backgroundColor: "#040c4b" }}
        >
          <div className="logo-wrapper">
            <img
              className=""
              src="/AcademyLogoBlue.png"
              style={{
                filter: "invert(1) grayscale(1) brightness(2)",
                width: "55px",
                height: "auto",
              }}
              alt="Al Hilal Academy Logo"
            />
          </div>
          <h5 className="mb-0 fw-bold text-white">Al Hilal Academy</h5>
        </div>

        {/* Content */}
        <div className="flex-grow-1 d-flex justify-content-center align-items-center">
          <div
            className="bg-white shadow p-4 text-center"
            style={{ maxWidth: 500, width: "100%", borderRadius: "1rem" }}
          >
            <div className="text-danger mb-3">
              <svg
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="mx-auto"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </div>
            <h4 className="fw-bold mb-2 text-danger">Payment Error</h4>
            <p className="text-muted mb-4">{error}</p>
            <button
              className="btn text-white"
              style={{ backgroundColor: "#040c4b" }}
              onClick={handleReturnHome}
            >
              Return to Home
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-muted py-3">
          © 2025 Al Hilal Academy. All rights reserved.
        </div>
      </div>
    );
  }

  // Determine if payment was successful
  const isSuccessful = paymentDetails?.isSuccessful || false;

  return (
    <div className="min-vh-100 bg-light d-flex flex-column">
      {/* Header */}
      <div
        className="text-white py-0 px-4 d-flex align-items-center"
        style={{ backgroundColor: "#040c4b" }}
      >
        <div className="logo-wrapper">
          <img
            className=""
            src="/AcademyLogoBlue.png"
            style={{
              filter: "invert(1) grayscale(1) brightness(2)",
              width: "55px",
              height: "auto",
            }}
            alt="Al Hilal Academy Logo"
          />
        </div>
        <h5 className="mb-0 fw-bold text-white">Al Hilal Academy</h5>
      </div>

      {/* Content */}
      <div className="flex-grow-1 d-flex justify-content-center align-items-center">
        <div
          className="bg-white shadow p-3 text-center"
          style={{ maxWidth: 600, width: "100%", borderRadius: "1rem" }}
        >
          {isSuccessful ? (
            // Success Case
            <>
              <div className="text-success mb-2">
                <svg
                  width="60"
                  height="60"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="mx-auto"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22,4 12,14.01 9,11.01"></polyline>
                </svg>
              </div>
              
              <h4 className="fw-bold mb-2 text-success">Payment Successful!</h4>
              <p className="text-muted mb-3">
                Thank you! Your payment has been processed successfully.
              </p>

              {/* Player Details Display */}
              {playerDetails && (
                <div className="card border-0 bg-light mb-3">
                  <div className="card-body p-3">
                    <h6 className="card-title text-primary mb-2">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className="me-2"
                      >
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                      Payment Details
                    </h6>
                    <div className="row g-2">
                      {/* Transaction ID */}
                      <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center py-1">
                          <span className="text-muted small">Transaction ID:</span>
                          <span className="fw-semibold font-monospace small">{playerDetails.transactionId || 'N/A'}</span>
                        </div>
                      </div>
                      {/* Transaction Date */}
                      <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center py-1">
                          <span className="text-muted small">Date:</span>
                          <span className="fw-semibold">
                            {playerDetails.transactionDate 
                              ? new Date(playerDetails.transactionDate).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })
                              : 'N/A'
                            }
                          </span>
                        </div>
                      </div>
                      {/* Amount Paid */}
                      <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center py-1">
                          <span className="text-muted small">Amount Paid:</span>
                          <span className="fw-semibold text-success">
                            {playerDetails.amountPaid 
                              ? `${playerDetails.currency || 'SAR'} ${playerDetails.amountPaid}`
                              : 'N/A'
                            }
                          </span>
                        </div>
                      </div>
                      <hr className="my-2" />
                      {/* Player Name */}
                      <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center py-1">
                          <span className="text-muted small">Player Name:</span>
                          <span className="fw-semibold">{playerDetails.playerName || 'N/A'}</span>
                        </div>
                      </div>
                      {/* Type - Show for Product payments */}
                      {playerDetails.paymentType === 'Product' && (
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center py-1">
                            <span className="text-muted small">Type:</span>
                            <span className="fw-semibold">Product Purchase</span>
                          </div>
                        </div>
                      )}
                      {/* Membership - Hide for Product payments */}
                      {playerDetails.paymentType !== 'Product' && (
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center py-1">
                            <span className="text-muted small">Membership:</span>
                            <span className="fw-semibold">{playerDetails.membershipName || 'N/A'}</span>
                          </div>
                        </div>
                      )}
                      {/* Start Date - Hide for Product payments */}
                      {playerDetails.paymentType !== 'Product' && (
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center py-1">
                            <span className="text-muted small">Start Date:</span>
                            <span className="fw-semibold">
                              {playerDetails.startDate 
                                ? new Date(playerDetails.startDate).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  })
                                : 'N/A'
                              }
                            </span>
                          </div>
                        </div>
                      )}
                      {/* Expiry Date - Hide for Product payments */}
                      {playerDetails.paymentType !== 'Product' && (
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center py-1">
                            <span className="text-muted small">Expiry Date:</span>
                            <span className="fw-semibold">
                              {playerDetails.expiryDate 
                                ? new Date(playerDetails.expiryDate).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  })
                                : 'N/A'
                              }
                            </span>
                          </div>
                        </div>
                      )}
                      {/* Branch Name */}
                      {playerDetails.branchName && (
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center py-1">
                            <span className="text-muted small">Branch:</span>
                            <span className="fw-semibold">{playerDetails.branchName}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Download Button Only */}
              <div className="mb-2">
                <button
                  className="btn text-white w-100"
                  style={{ backgroundColor: "rgb(4, 12, 75)" }}
                  onClick={handleDownloadReceipt}
                  disabled={downloadingReceipt}
                >
                  {downloadingReceipt ? (
                    <>
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      Downloading PDF...
                    </>
                  ) : (
                    <>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className="me-2"
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7,10 12,15 17,10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                      Download Receipt PDF
                    </>
                  )}
                </button>
              </div>
            </>
          ) : (
            // Failure Case
            <>
              <div className="text-danger mb-3">
                <svg
                  width="80"
                  height="80"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="mx-auto"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
              </div>
              
              <h2 className="fw-bold mb-3 text-danger">Payment Failed</h2>
              <p className="text-muted mb-4">
                We're sorry, but your payment could not be processed at this time.
              </p>

              {paymentDetails?.result && (
                <div className="mb-4">
                  <div className="alert alert-danger" role="alert">
                    <small>
                      <strong>Error Details:</strong>
                      <br />
                      {paymentDetails.result.description || "Payment processing failed"}
                      {paymentDetails.result.code && (
                        <>
                          <br />
                          <strong>Error Code:</strong> {paymentDetails.result.code}
                        </>
                      )}
                    </small>
                  </div>
                </div>
              )}

              <div className="alert alert-info" role="alert">
                <small>
                  <strong>What can you do?</strong>
                  <br />
                  • Check your card details and try again
                  <br />
                  • Try a different payment method
                  <br />
                  • Contact your bank if the issue persists
                  <br />
                  • Contact our support team for assistance
                </small>
              </div>

              <div className="row gx-3">
                <div className="col">
                </div>
                <div className="col">
                  <button
                    className="btn text-white w-100"
                    style={{ backgroundColor: "#040c4b" }}
                    onClick={handleTryAgain}
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="text-center text-muted py-3">
        © 2025 Al Hilal Academy. All rights reserved.
      </div>
    </div>
  );
};

export default PaymentResult;
